'use client';

import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

type UsePopoverFocusRestorationArgs = {
    triggerRef?: RefObject<Element | null>;
    isOpen?: boolean;
    defaultOpen?: boolean;
};

/**
 * Gets the element that should receive focus when a standalone controlled popover closes.
 */
export const getPopoverFocusRestoreTarget = ({
    activeElement,
    triggerElement,
}: {
    activeElement: Element | null;
    triggerElement: HTMLElement | null;
}): HTMLElement | null => {
    if (activeElement instanceof HTMLElement) return activeElement;
    return triggerElement;
};

/**
 * Returns true when focus should be restored to the stored target after the popover closes.
 */
export const shouldRestorePopoverFocus = ({
    activeElement,
    restoreTarget,
}: {
    activeElement: Element | null;
    restoreTarget: HTMLElement | null;
}): boolean => {
    if (!restoreTarget || !restoreTarget.isConnected) return false;
    if (!activeElement) return true;
    if (activeElement === restoreTarget) return false;

    return activeElement === document.body || activeElement === document.documentElement;
};

export const usePopoverFocusRestoration = ({ triggerRef, isOpen, defaultOpen }: UsePopoverFocusRestorationArgs) => {
    const restoreTargetRef = useRef<HTMLElement | null>(null);
    const wasOpenRef = useRef<boolean>(Boolean(isOpen ?? defaultOpen));
    const isControlled = typeof isOpen === 'boolean';

    useEffect(() => {
        if (!triggerRef || !isControlled) {
            wasOpenRef.current = Boolean(isOpen ?? defaultOpen);
            return;
        }

        if (isOpen) {
            restoreTargetRef.current = getPopoverFocusRestoreTarget({
                activeElement: document.activeElement,
                triggerElement: triggerRef.current instanceof HTMLElement ? triggerRef.current : null,
            });
            wasOpenRef.current = true;
            return;
        }

        if (!wasOpenRef.current) {
            wasOpenRef.current = false;
            return;
        }

        const restoreTarget = restoreTargetRef.current;
        if (!restoreTarget || !restoreTarget.isConnected) {
            wasOpenRef.current = false;
            return;
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            if (
                shouldRestorePopoverFocus({
                    activeElement: document.activeElement,
                    restoreTarget,
                })
            ) {
                restoreTarget.focus({ preventScroll: true });
            }
        });

        wasOpenRef.current = false;

        return () => {
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [defaultOpen, isControlled, isOpen, triggerRef]);
};
